import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { FormControl } from '@angular/forms';

export interface PersonOption {
    _id: string;
    /** "Nachname Vorname". */
    name: string;
}

interface YearOption {
    id: number;
    name: string;
}

/**
 * REV-22155 — "+ Neues Projekt" for persons, in the look of `new-project-wizard`:
 * 1. choose the person (skipped when `personId` is given, e.g. from the person's "Projekte" tab)
 * 2. choose the tax period — a year only; the next year after the person's latest project, else last year
 * No report type. Pure view: reports `create` / `closeButtonClicked`.
 */
@Component({
    selector: 'new-person-project-wizard',
    templateUrl: 'new-person-project-wizard.component.html',
    styleUrls: ['new-project-wizard.component.scss', 'new-person-project-wizard.component.scss'],
})
export class NewPersonProjectWizardComponent implements OnChanges {
    /** Active persons only (`GET /persons?onlyNameId=true`). */
    @Input() personsList: PersonOption[] = [];
    @Input() personId: string | null = null;
    /** Year of each person's latest project; persons without a project are missing. */
    @Input() lastProjectYearByPersonId: Record<string, number> = {};

    @Output() create = new EventEmitter<{ personId: string; year: number }>();
    @Output() closeButtonClicked = new EventEmitter<boolean>();

    readonly personControl = new FormControl<PersonOption | null>(null);
    readonly yearControl = new FormControl<YearOption | null>(null);
    pageNumber = 1;
    yearOptions: YearOption[] = [];

    get selectedYear(): number | null {
        return this.yearControl.value?.id ?? null;
    }

    get totalNumberOfPages(): number {
        return this.personId != null ? 1 : 2;
    }

    get pageLabel(): number {
        return this.personId != null ? 1 : this.pageNumber;
    }

    ngOnChanges(): void {
        if (this.personId != null) {
            this.pageNumber = 2;
            this.preselectYear(this.personId);
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.onCancel();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (this.pageNumber === 1 && this.personControl.value != null) {
                this.next();
            } else if (this.pageNumber === 2 && this.selectedYear != null) {
                this.submit();
            }
        }
    }

    next(): void {
        const person = this.personControl.value;
        if (person == null) {
            return;
        }
        this.preselectYear(person._id);
        this.pageNumber = 2;
    }

    goBack(): void {
        this.pageNumber = 1;
    }

    submit(): void {
        const personId = this.personId ?? this.personControl.value?._id;
        if (personId != null && this.selectedYear != null) {
            this.create.emit({ personId, year: this.selectedYear });
        }
    }

    onCancel(): void {
        this.closeButtonClicked.emit(true);
    }

    /** The person already has a project → the following year, else last year. */
    private defaultYear(personId: string): number {
        const lastYear = this.lastProjectYearByPersonId[personId];
        return lastYear != null ? lastYear + 1 : new Date().getFullYear() - 1;
    }

    private preselectYear(personId: string): void {
        const year = this.defaultYear(personId);
        this.yearOptions = this.buildYearOptions(year);
        this.yearControl.setValue(this.yearOptions.find((option) => option.id === year) ?? null);
    }

    /** Next year back to 15 years ago, plus the preselected one. */
    private buildYearOptions(preselected: number): YearOption[] {
        const current = new Date().getFullYear();
        const years = new Set<number>([preselected]);
        for (let year = current + 1; year >= current - 15; year--) {
            years.add(year);
        }
        return Array.from(years)
            .sort((a, b) => b - a)
            .map((year) => ({ id: year, name: String(year) }));
    }
}
